import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import type { FileWithPreview } from '@/types';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// Format currency
export function formatCurrency(amount: number, currency = 'USD'): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
  }).format(amount / 100);
}

// Validate email format
export function isValidEmail(email: string): boolean {
  const re = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  return re.test(String(email).toLowerCase());
}

// Validate file size
export function isValidFileSize(file: File, maxSizeMB = 10): boolean {
  return file.size <= maxSizeMB * 1024 * 1024;
}

// Validate file type
export function isValidFileType(file: File, allowedTypes: string[]): boolean {
  return allowedTypes.includes(file.type);
}

// Sanitize file name
export function sanitizeFileName(fileName: string): string {
  // Remove special characters and spaces, keep the extension
  const extension = fileName.slice(fileName.lastIndexOf('.'));
  const nameWithoutExt = fileName.slice(0, fileName.lastIndexOf('.'));
  
  // Replace special characters and spaces with hyphens
  const sanitized = nameWithoutExt
    .replace(/[^a-zA-Z0-9-_]/g, '-') // Replace special chars with hyphen
    .replace(/-+/g, '-') // Replace multiple hyphens with single hyphen
    .replace(/^-+|-+$/g, ''); // Remove hyphens from start and end
  
  return sanitized + extension;
}

/**
 * Attach a browser object URL to a file, under its sanitized name.
 *
 * Every URL this hands out has to be released again by revokeFilePreviews,
 * or the browser holds the file's bytes until the tab closes.
 */
export function createFilePreview(file: File): FileWithPreview {
  const renamed = new File([file], sanitizeFileName(file.name), { type: file.type });
  return Object.assign(renamed, { preview: URL.createObjectURL(renamed) });
}

// Release the object URLs created by createFilePreview.
export function revokeFilePreviews(files: readonly { preview?: string }[]): void {
  files.forEach(file => {
    if (file?.preview) {
      URL.revokeObjectURL(file.preview);
    }
  });
}

// Truncate text with ellipsis
export function truncateText(text: string, maxLength: number): string {
  return text.length > maxLength
    ? `${text.substring(0, maxLength)}...`
    : text;
}

// Generate a cryptographically secure random ID
export function generateId(length = 8): string {
  const array = new Uint8Array(length);
  crypto.getRandomValues(array);
  return Array.from(array, byte => byte.toString(36).padStart(2, '0'))
    .join('')
    .substring(0, length);
}

// Delay execution (for animations, etc.)
export function delay(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}